import { Box, IconButton, Tooltip, Typography, Paper, useTheme, Button, ButtonGroup, CircularProgress } from '@mui/material'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import CustomSearchBar from '../../components/common/CustomSearchBar'
import ListView from '../../components/exams-components/ListView'
import GridView from '../../components/exams-components/GridView'
import { useNavigate, useSearchParams } from 'react-router-dom'
import GridViewIcon from '@mui/icons-material/GridView'
import { useUserStore } from '../../store/user.store'
import { useExamStore } from '../../store/exam.store'
import React, { useCallback, useEffect, useState } from 'react'
import noExamImg from '../../assets/noexams.svg'
import { formatDate, Icons } from '../../utils/utilities'
import { observer } from 'mobx-react'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import { toast } from 'react-toastify'
import api from '../../service/api'
import CountCard from '../../components/common/CountCard'
import AddExam from './faculty/AddExam'

const Exams = observer( () => {

    const [retrievingLegacyData, setRetrievingLegacyData] = useState( false )
    const [createMode, setCreateMode] = useState( false )

    const [searchParams, setSearchParams] = useSearchParams( { selectedExam: "", mode: "manage", searchText: "", instituteCode: "0", view: window.localStorage.getItem( 'aliveExamsViewType' ) ? window.localStorage.getItem( 'aliveExamsViewType' ) : "grid" } )
    const mode = searchParams.get( 'mode' ) || "manage"
    const searchText = searchParams.get( "searchText" ) || ""
    const view = searchParams.get( "view" ) || "grid"

    const navigate = useNavigate()
    const UserStore = useUserStore()
    const ExamStore = useExamStore()

    const [inProgress, setInProgresss] = useState( true )

    const { palette, border, layout } = useTheme()

    const setSearchParamValue = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const retrieveOldExams = async () => {
        try {
            setRetrievingLegacyData( true )
            await api.retrieveLegacyAliveExams()
            setInProgresss( true )
            await UserStore.fetchUser()
            await ExamStore.fetchExams()
            setInProgresss( false )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while retriving legacy exams!" )
        } finally {
            setRetrievingLegacyData( false )
        }
    }

    const getExams = useCallback( async () => {
        await ExamStore.fetchExams()
        setInProgresss( false )
    }, [ExamStore] )

    useEffect( () => {
        getExams()
    }, [getExams] )

    const latestExam = ExamStore.getExams.reduce( ( latestExam, exam ) => {
        return ( exam.age > latestExam.age ) ? exam : latestExam
    }, ExamStore.getExams[0] )

    return (
        <Box flexGrow={1} padding={layout.pagePadding} display="flex" flexDirection="column" overflow="auto">

            {/* Create Dialog */}
            <AddExam open={createMode} setOpen={setCreateMode} />

            <Box flexGrow={1} sx={{ display: "flex", flexDirection: "column" }} >
                <Box gap={layout.sectionsGap} display="flex" flexDirection="column" justifyContent="space-between">
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Exams</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Manage your exams from here.</Typography>
                    </Box>

                    {latestExam && <Box display="grid" gap={layout.cardsGap} width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                        <CountCard loading={inProgress} label='Total Exams' icon={Icons.default.QuizIcon} value={ExamStore.getExams.length} />
                        <CountCard loading={inProgress} onClick={() => UserStore.getUser.user_role === "STUDENT" ? navigate( `/student/exams/${latestExam.exam_id}` ) : navigate( `/faculty/exams/${latestExam.exam_id}` )} label='Latest Exam' icon={Icons.default.Last} value={latestExam?.exam_name} />
                    </Box>}

                    {!inProgress && !ExamStore.getErrors.exams && <Box flexWrap="wrap" display="flex" justifyContent="space-between" gap="10px">
                        <CustomSearchBar border={border[1]} style={{ flexGrow: 1 }} onChange={( e ) => setSearchParamValue( 'searchText', e.target.value )} value={searchText} />
                        <Box display="flex" flexWrap="wrap" gap="10px" alignItems="stretch">
                            <ButtonGroup size='small' sx={{ background: palette.contentBg, borderRadius: "5px", padding: "3px" }}>
                                <Tooltip title="List view">
                                    <IconButton size='small'
                                        color={view === 'list' ? "primary" : "greyed"}
                                        onClick={() => {
                                            setSearchParamValue( 'view', 'list' )
                                            window.localStorage.setItem( 'aliveExamsViewType', 'list' )
                                        }}
                                    >
                                        <FormatListBulletedIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Grid view">
                                    <IconButton size='small' color={view === 'grid' ? "primary" : "greyed"}
                                        onClick={() => {
                                            setSearchParamValue( 'view', 'grid' )
                                            window.localStorage.setItem( 'aliveExamsViewType', 'grid' )
                                        }}
                                    >
                                        <GridViewIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </ButtonGroup>

                            {UserStore.getUser.user_role !== 'STUDENT' && ( !mode || mode === "manage" ) &&
                                <Box display="flex" gap="10px">
                                    <Button sx={{ textTransform: "capitalize", flexGrow: 1 }} color='primary' disableElevation onClick={() => setCreateMode( true )} variant='contained' startIcon={Icons.default.AddIcon}>Create New Exam</Button>
                                    {!UserStore.getUser?.data_sync_status?.exams && <Button sx={{ textTransform: "capitalize", flexGrow: 1 }} disabled={retrievingLegacyData} onClick={retrieveOldExams} color='secondary' variant='outlined' startIcon={retrievingLegacyData ? <CircularProgress size={14} /> : Icons.default.Replay}>Retrieve my old Exams</Button>}
                                </Box>
                            }
                        </Box>
                    </Box>}
                </Box>

                {!inProgress && !ExamStore.getErrors.exams && ( !mode || mode === "manage" ) && ExamStore.getExams.length !== 0 && <Box className="custom-scrollbar" marginTop="10px" flexGrow={1} display="flex" flexDirection="column">
                    {view === 'grid' && <GridView exams={ExamStore.getExams} userRole={UserStore.getUser.user_role} />}
                    {view === 'list' && <ListView exams={ExamStore.getExams} userRole={UserStore.getUser.user_role} />}
                </Box>}

                {inProgress && <DataLoadingSpinner padding='20px 0' waitingMessage="Loading exams..." />}
                {
                    !inProgress && !ExamStore.getErrors.exams && ( ExamStore.getExams.length === 0 ) && <Box flexGrow={1} borderRadius="5px" flexDirection="column" display="flex" justifyContent="center" alignItems="center" padding="20px" bgcolor={palette.contentBg} margin="10px" gap="20px" color="textSecondary" variant='subtitle2'>
                        <Box component="img" src={noExamImg} width="200px" alt="no exams!" />
                        <Typography variant='subtitle2'>No exams to show!</Typography>
                        {UserStore.getUser.user_role !== 'STUDENT' ? ( !mode || mode === "manage" ) ? <Button sx={{ textTransform: "capitalize" }} variant='outlined' startIcon={Icons.default.AddIcon} onClick={() => setCreateMode( true )} >Create One</Button> : "" : ""}
                    </Box>
                }

                {!inProgress && ExamStore.getErrors.exams && <Box flexGrow={1} display="flex" >
                    <TryAgainMessageBlock styles={{ margin: "20px", flexGrow: 1 }} err='Something went wrong while fetching exams.' parent='component' getData={getExams} loading={inProgress} /></Box>
                }

            </Box >
        </Box >
    )
} )

export default Exams
